import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist'
import storage from 'redux-persist/lib/storage'

import { attributesSlice, initialAttributesState } from '@/entities/attributes'
import { columnDefinitionsSlice, initialColDefState } from '@/entities/columnDefinitions'
import { compartmentsSlice, initialCompartmentState } from '@/entities/compartments'
import { constraintsSlice, initialConstraintState } from '@/entities/constraints/constraintsSlice'
import { depotsSlice, initialDepotState } from '@/entities/depots'
import { driversSlice, initialDriverState } from '@/entities/drivers'
import { dropdownsSlice, initialDropdownState } from '@/entities/dropdowns/dropdownsSlice'
import { initialPlanningInboxTableState, planningInboxTableSlice } from '@/entities/gridTables/slices/planningInboxTableSlice'
import { initialPlanningShiftTableState, planningShiftTablesSlice } from '@/entities/gridTables/slices/planningShiftTablesSlice'
import { initialPlanningUnassignedTableState, planningUnassignedTablesSlice } from '@/entities/gridTables/slices/planningUnassignedTablesSlice'
import { initialRunsheetShiftTableState, runsheetShiftTablesSlice } from '@/entities/gridTables/slices/runsheetShiftTablesSlice'
import { initialRunsheetUnassignedTableState, runsheetUnassignedTablesSlice } from '@/entities/gridTables/slices/runsheetUnassignedTablesSlice'
import { groupsSlice, initialGroupState } from '@/entities/groups'
import { initialOrderState, ordersSlice } from '@/entities/orders'
import { initialRunState, runsSlice } from '@/entities/runs'
import {
  initialPlanningShiftState, initialPlanningStopState, initialRunsheetShiftState, initialRunsheetState, initialScenarioState, planningShiftsSlice,
  planningStopsSlice, runsheetShiftsSlice, runsheetsSlice, scenariosSlice,
} from '@/entities/schedules'
import { initialRunsheetStopState, runsheetStopsSlice } from '@/entities/schedules/slices/runsheetStopsSlice'
import { initialSubscriptionState, subscriptionSlice } from '@/entities/subscription'
import { initialToastState, toastSlice } from '@/entities/toast'
import { sessionInitialState, sessionSlice, userInitialState, userSlice } from '@/entities/user'
import { userInterfaceSlice } from '@/entities/userInterface'
import { initialLoaderState, loaderSlice } from '@/entities/userInterface/slices/loaderSlice'
import { initialUserInterfaceState } from '@/entities/userInterface/slices/userInterfaceSlice'
import { initialWebAdminState, webAdminsSlice } from '@/entities/webAdmins/webAdminsSlice'
import { initialWidgetWindowState, widgetWindowsSlice } from '@/entities/widgetWindows'
import { initialPlanningPageState, planningPageSlice } from '@/pages/Operations/slices/planningPageSlice'
import { initialRunsheetPageState, runsheetPageSlice } from '@/pages/Operations/slices/runsheetPageSlice'
import { baseApi } from '@/shared/apis'
import { combineReducers, configureStore } from '@reduxjs/toolkit'

import { RESET_STATE } from './action/resetState'

// Create root reducer
export const appReducer = combineReducers({
  session: sessionSlice.reducer,
  user: userSlice.reducer,
  subscription: subscriptionSlice.reducer,
  groups: groupsSlice.reducer,
  columnDefinitions: columnDefinitionsSlice.reducer,
  orders: ordersSlice.reducer,
  depots: depotsSlice.reducer,
  drivers: driversSlice.reducer,
  runs: runsSlice.reducer,
  webAdmins: webAdminsSlice.reducer,
  attributes: attributesSlice.reducer,
  constraints: constraintsSlice.reducer,
  compartments: compartmentsSlice.reducer,

  // Planning
  planningPage: planningPageSlice.reducer, // Client-specific slice
  scenarios: scenariosSlice.reducer,
  planningShifts: planningShiftsSlice.reducer,
  planningStops: planningStopsSlice.reducer,

  // Runsheet
  runsheetPage: runsheetPageSlice.reducer, // Client-specific slice
  runsheets: runsheetsSlice.reducer,
  runsheetStops: runsheetStopsSlice.reducer,
  runsheetShifts: runsheetShiftsSlice.reducer,

  // Client-specific (UI) slices
  loader: loaderSlice.reducer,
  userInterface: userInterfaceSlice.reducer,
  widgetWindows: widgetWindowsSlice.reducer,
  toast: toastSlice.reducer,
  
  planningShiftTables: planningShiftTablesSlice.reducer,
  planningUnassignedTables: planningUnassignedTablesSlice.reducer,
  planningInboxRows: planningInboxTableSlice.reducer,

  runsheetShiftTables: runsheetShiftTablesSlice.reducer,
  runsheetUnassignedTables: runsheetUnassignedTablesSlice.reducer,

  // Dropdown helpers
  dropdowns: dropdownsSlice.reducer,
  
  // RTK Query API
  [baseApi.reducerPath]: baseApi.reducer,
})

export const rootReducer: typeof appReducer = (state, action) => {
  if (action.type === RESET_STATE) {
    return appReducer({
      session: sessionInitialState,
      user: userInitialState,
      subscription: initialSubscriptionState,
      groups: initialGroupState,
      depots: initialDepotState,
      runs: initialRunState,
      webAdmins: initialWebAdminState,
      drivers: initialDriverState,
      columnDefinitions: initialColDefState,
      orders: initialOrderState,
      attributes: initialAttributesState,
      constraints: initialConstraintState,
      dropdowns: initialDropdownState,
      compartments: initialCompartmentState,
      
      // Planning
      planningPage: initialPlanningPageState,
      scenarios: initialScenarioState,
      planningStops: initialPlanningStopState,
      planningShifts: initialPlanningShiftState,

      // Runsheet
      runsheetPage: initialRunsheetPageState,
      runsheets: initialRunsheetState,
      runsheetStops: initialRunsheetStopState,
      runsheetShifts: initialRunsheetShiftState,
      
      // Client-specific (UI) slices
      loader: initialLoaderState,
      userInterface: {
        ...initialUserInterfaceState,
        activeGroupId: state?.userInterface?.activeGroupId || initialUserInterfaceState.activeGroupId,
      },
      widgetWindows: initialWidgetWindowState,
      planningShiftTables: initialPlanningShiftTableState,
      planningUnassignedTables: initialPlanningUnassignedTableState,
      planningInboxRows: initialPlanningInboxTableState,
      runsheetUnassignedTables: initialRunsheetUnassignedTableState,
      runsheetShiftTables: initialRunsheetShiftTableState,
      toast: initialToastState,

      // RTK Query API
      [baseApi.reducerPath]: state?.[baseApi.reducerPath],
    }, action)
  }

  return appReducer(state, action)
}

export type RootState = ReturnType<typeof rootReducer>

// Combine root reducer with persist reducer.
const persistConfig = {
  key: 'root',
  version: 1,
  storage: storage,
  blacklist: [],
  whitelist: ['user', 'session', 'subscription', 'userInterface', 'widgetWindows'],
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createStore = (state: any = {}) => {
  return configureStore({
    reducer: persistedReducer,
    preloadedState: state,
    middleware: getDefaultMiddleware => getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(baseApi.middleware),
  })
}

export const store = createStore()
export type AppDispatch = typeof store.dispatch

// Thunk API context used in thunks
export type ThunkApiContext = {
  state: RootState
  dispatch: AppDispatch
}

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const persistor = persistStore(store)
